import React from 'react';
import { Link } from 'react-router-dom';
import { functionDoc, legacyToFunction } from '../../manual/functionDoc';
import { MANUAL_SHOW_SCREENSHOT_PLACEHOLDERS } from '../../manual';

function normalizeSection(raw) {
  if (raw.docVersion === 2) return raw;
  if (raw.generalDescription && raw.name) {
    return functionDoc({
      ...raw,
      type: raw.type || raw.functionType || 'form',
    });
  }
  return legacyToFunction(raw);
}

function ScreenshotPlaceholder({ caption, index }) {
  return (
    <figure className="manual-screenshot">
      <div className="manual-screenshot-box" aria-hidden>
        [Insertar captura de pantalla {index + 1}]
      </div>
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

export default function ManualFunctionSection({ section: raw, functionTitleById = {} }) {
  const sec = normalizeSection(raw);
  const typeLabel =
    sec.functionType === 'report'
      ? 'Reporte / consulta'
      : sec.functionType === 'list'
        ? 'Listado / consulta'
        : sec.functionType === 'config'
          ? 'Configuración'
          : sec.functionType === 'screen'
            ? 'Pantalla'
            : 'Registro / formulario';

  return (
    <section className="manual-function">
      <h3 className="manual-h3">{sec.name}</h3>
      <p className="manual-fn-type text-xs text-slate-500 uppercase tracking-wide">{typeLabel}</p>

      <div className="manual-fn-block">
        <h4 className="manual-h4">1. Nombre de la función</h4>
        <p>{sec.name}</p>
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">2. Descripción general</h4>
        <p>{sec.generalDescription || sec.purpose}</p>
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">3. Ubicación en el sistema</h4>
        <p className="font-medium text-teal-900">{sec.locationPath || '—'}</p>
        {sec.route && (
          <p className="manual-route mt-1">
            Acceso directo:{' '}
            <Link to={sec.route} className="text-teal-700 hover:underline">{sec.route}</Link>
          </p>
        )}
      </div>

      {sec.roles && (
        <div className="manual-fn-block">
          <h4 className="manual-h4">Permisos y roles</h4>
          <p>{sec.roles}</p>
        </div>
      )}

      <div className="manual-fn-block">
        <h4 className="manual-h4">4. Requisitos previos</h4>
        {sec.prerequisites?.length ? (
          <ul className="manual-list">
            {sec.prerequisites.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-500 text-sm">No aplica o ninguno adicional documentado.</p>
        )}
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">5. Descripción de la interfaz</h4>
        {sec.interfaceElements?.length > 0 ? (
          <div className="manual-table-wrap">
            <table className="manual-table">
              <thead>
                <tr>
                  <th>Elemento</th>
                  <th>Tipo</th>
                  <th>Obl.</th>
                  <th>Editable</th>
                  <th>Automático</th>
                  <th>Propósito y comportamiento</th>
                </tr>
              </thead>
              <tbody>
                {sec.interfaceElements.map((el) => (
                  <tr key={el.name}>
                    <td className="manual-td-field">{el.name}</td>
                    <td className="text-xs">{el.controlType || '—'}</td>
                    <td className="text-center text-xs">{el.required ? 'Sí' : 'No'}</td>
                    <td className="text-center text-xs">
                      {el.editable === false ? 'No' : 'Sí'}
                    </td>
                    <td className="text-center text-xs">{el.automatic ? 'Sí' : 'No'}</td>
                    <td>
                      {el.description}
                      {el.example ? (
                        <span className="block text-xs text-slate-500 mt-1">Ejemplo: {el.example}</span>
                      ) : null}
                      {el.behavior ? (
                        <span className="block text-xs text-slate-600 mt-1">{el.behavior}</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-amber-800 text-sm">
            Pendiente de detallar elementos de interfaz en el manual (verificar en pantalla).
          </p>
        )}
        {MANUAL_SHOW_SCREENSHOT_PLACEHOLDERS &&
          sec.screenshots?.length > 0 &&
          sec.screenshots.map((cap, i) => <ScreenshotPlaceholder key={i} caption={cap} index={i} />)}
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">6. Procedimiento paso a paso</h4>
        <ol className="manual-steps">
          {(sec.procedure || sec.steps || []).map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">7. Ejemplo práctico</h4>
        {sec.practicalExample ? (
          typeof sec.practicalExample === 'string' ? (
            <p>{sec.practicalExample}</p>
          ) : (
            <>
              {sec.practicalExample.context && <p>{sec.practicalExample.context}</p>}
              {sec.practicalExample.data && (
                <ul className="manual-list">
                  {Object.entries(sec.practicalExample.data).map(([k, v]) => (
                    <li key={k}>
                      <strong>{k}:</strong> {v}
                    </li>
                  ))}
                </ul>
              )}
              {sec.practicalExample.steps?.length > 0 && (
                <ol className="manual-steps">
                  {sec.practicalExample.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              )}
            </>
          )
        ) : (
          <p className="text-slate-500 text-sm">Ver procedimiento y datos de ejemplo en validaciones.</p>
        )}
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">8. Validaciones y restricciones</h4>
        <ul className="manual-list">
          {(sec.validations || []).map((v) => (
            <li key={v}>{v}</li>
          ))}
        </ul>
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">9. Resultado esperado</h4>
        <ul className="manual-list">
          {(sec.expectedResult || []).map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">10. Errores frecuentes y soluciones</h4>
        {sec.commonErrors?.length > 0 ? (
          <div className="manual-table-wrap">
            <table className="manual-table">
              <thead>
                <tr>
                  <th>Mensaje / síntoma</th>
                  <th>Causa probable</th>
                  <th>Qué hacer</th>
                </tr>
              </thead>
              <tbody>
                {sec.commonErrors.map((row, i) => (
                  <tr key={i}>
                    <td>{row.error || row.message}</td>
                    <td>{row.cause}</td>
                    <td>{row.solution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-slate-500 text-sm">Sin errores documentados; reporte al administrador si persiste.</p>
        )}
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">11. Relación con otras funciones</h4>
        <ul className="manual-list">
          {(sec.relatedFunctions || []).map((r) => (
            <li key={r}>
              <a href={`#${r}`} className="text-teal-700 hover:underline">
                {functionTitleById[r] || r}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="manual-fn-block">
        <h4 className="manual-h4">12. Observaciones y recomendaciones</h4>
        <ul className="manual-list manual-notes">
          {(sec.observations || sec.notes || []).map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}
